<script setup lang="ts" generic="T = string | number | { name: string; [key: string]: any }">
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { ChevronDownIcon } from '@heroicons/vue/20/solid'
import { useField } from 'vee-validate'
import { computed, onMounted, onUnmounted, ref, useAttrs, useTemplateRef, watch } from 'vue'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import { useDropdownTeleportPosition } from '@/composables/useDropdownTeleportPosition'
import { translationFunctionDefault } from '@/utilities/translations'

import type { DropdownOption, SingleSelectProps } from './inputTypes'

import AvatarInitials from '../avatar/AvatarInitials.vue'
import AvatarOrganization from '../avatar/AvatarOrganization.vue'
import ButtonTertiary from '../buttons/ButtonTertiary.vue'

const {
  preselectValue = undefined,
  searchable = false,
  ...props
} = defineProps<SingleSelectProps<T> & { preselectValue?: T }>()

const emit = defineEmits<{
  blur: []
  change: [T]
  manualInput: []
}>()
defineOptions({ inheritAttrs: false })
const attributesUse = useAttrs()

const tfDefault = translationFunctionDefault()
const input = useTemplateRef('my-input')
const menuButton = useTemplateRef<{ $el?: HTMLElement }>('menu-button')
const inputDirty = ref(false)

const { dropdownStyle, dropdownTarget, onDropdownEnter, removeDropdownListeners } =
  useDropdownTeleportPosition({
    direction: () => props.direction,
    enabled: () => props.teleportDropdown,
    getButtonElement: () => menuButton.value?.$el,
  })

const isObjectWithName = (value: unknown): value is { name: string } => {
  return typeof value === 'object' && value !== null && 'name' in value
}

const {
  errorMessage: vErrorMessage,
  value: vValue,
  // we need this because eslint would remove <T> from useField
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments
} = useField<T>(() => {
  return props.name ?? ''
})

const model = defineModel<T>('selected', {
  get: (value) => {
    return props.name ? vValue.value : value
  },
  set: (value) => {
    if (props.name) {
      vValue.value = value
    }

    return value
  },
})

const selectedOption = computed(() => {
  const flatOptions = props.options.flat()
  const selected = flatOptions.find((option) => {
    if (isObjectWithName(option.optionValue) && isObjectWithName(model.value)) {
      return option.optionValue.name === model.value.name
    }
    return option.optionValue === model.value
  })

  if (selected) return selected

  if (preselectValue !== undefined) {
    return flatOptions.find((option) => option.optionValue === preselectValue)
  }

  // eslint-disable-next-line @typescript-eslint/consistent-return
  return undefined
})

// we dont need reactivity here
// eslint-disable-next-line vue/no-ref-object-reactivity-loss
const searchTerm = ref<string | undefined>(selectedOption.value?.optionLabel)
const filteredOptions = computed(() => {
  return props.options
    .map((section) => {
      return section.filter((option: DropdownOption<T>) => {
        if (!!searchTerm.value && searchable && inputDirty.value) {
          const searchTermLower = searchTerm.value.toLowerCase()
          const optionLabel = option.optionLabel.toLowerCase()

          return optionLabel.includes(searchTermLower)
        }

        return true
      })
    })
    .filter((section) => {
      return section.length > 0
    })
})

const existsAtLeastOneIconInOptions = computed(() => {
  return (
    props.options.flat().some((option: DropdownOption<T>) => {
      return !!option.icon
    }) || props.showAvatars
  )
})

const showEmptyStateAction = computed(() => {
  if (!props.emptyStateAction) return false
  if (props.alwaysShowEmptyStateAction) return true
  return filteredOptions.value.length === 0 && searchTerm.value !== ''
})

const onEmptyStateActionClick = (): void => {
  props.emptyStateAction?.action(searchTerm.value ?? '')
}

const onItemSelected = (val: T): void => {
  model.value = val
  emit('blur')
  emit('manualInput')
}

const onAfterEnter = (): void => {
  if (searchable) {
    input.value?.focus()
    input.value?.select()
  }
}

const onAfterLeave = (): void => {
  removeDropdownListeners()

  if (searchable) {
    inputDirty.value = false
    searchTerm.value = selectedOption.value?.optionLabel
  }
}

onUnmounted(() => {
  removeDropdownListeners()
})

const onKeyDown = (event: KeyboardEvent): void => {
  if (event.key === 'Enter') {
    return
  }
  inputDirty.value = true
  event.stopPropagation()
}

watch(selectedOption, (newValue) => {
  if (newValue) {
    searchTerm.value = newValue.optionLabel
  } else {
    searchTerm.value = undefined
  }
})

watch(
  () => {
    return model.value
  },
  (newValue) => {
    if (newValue !== undefined) {
      emit('change', newValue)
    }
  },
)

watch(
  () => preselectValue,
  (newVal) => {
    const noValueSet =
      vValue.value === undefined ||
      vValue.value === null ||
      vValue.value === '' ||
      (typeof vValue.value === 'object' && Object.keys(vValue.value).length === 0)

    if (props.name && noValueSet && newVal !== undefined) {
      vValue.value = newVal
    }
  },
)

onMounted(() => {
  const noValueSet =
    vValue.value === undefined ||
    vValue.value === null ||
    vValue.value === '' ||
    (typeof vValue.value === 'object' && Object.keys(vValue.value).length === 0)

  if (props.name && noValueSet && preselectValue !== undefined && preselectValue !== '') {
    vValue.value = preselectValue
  }
})
</script>

<template>
  <Menu
    as="div"
    class="menu group w-full space-y-1.5"
    :data-has-error="!!vErrorMessage"
    :data-testid="attributesUse['data-testid'] ?? testId"
  >
    <label
      v-if="label"
      class="text-sm"
      :class="[
        props.disabled
          ? 'text-black-400'
          : vErrorMessage
            ? 'text-radical_red-500'
            : 'text-black-900',
      ]"
      for=""
    >
      {{ label }} {{ props.required ? '*' : '' }}
    </label>

    <MenuButton
      ref="menu-button"
      class="relative inline-flex w-full items-center gap-x-1.5 rounded-lg text-sm"
      v-bind="$attrs"
      :class="[
        onWhiteBg ? 'border' : 'shadow',
        vErrorMessage ? 'border-radical_red-500' : 'border-black-200',
        small ? 'px-2.5 py-0' : 'px-5 py-3',
        props.disabled
          ? 'cursor-not-allowed border-black-100 bg-white text-black-400 opacity-60'
          : 'cursor-pointer bg-white text-black-900 outline-offset-[-2px] group-focus-within:outline group-focus-within:outline-2 group-focus-within:outline-lochmara-300 group-hover:outline group-hover:outline-2 group-hover:outline-lochmara-600',
      ]"
      :data-testid="TEST_IDS_CREATE.singleSelect.trigger"
      :disabled="props.disabled ?? false"
      :has-error="!!vErrorMessage"
      type="button"
    >
      <div class="flex w-full flex-1 items-center justify-between gap-2.5">
        <div
          class="flex w-full flex-1 items-center justify-start gap-2.5"
          :class="props.isFilledByAi ? 'italic text-lochmara-600' : ''"
        >
          <AvatarInitials
            v-if="showAvatars && !!selectedOption && !selectedOption.isHideIcon"
            :image="isResponsibleEmployee ? selectedOption?.image : undefined"
            :is-responsible-employee="isResponsibleEmployee"
            :name="selectedOption?.optionLabel || ''"
            :size="small ? 'tiny' : 'small'"
          />
          <AvatarOrganization
            v-if="isOrganization && !!selectedOption && !selectedOption.isHideIcon"
            :image="selectedOption?.image"
            size="small"
          />
          <component :is="icon" v-if="icon" class="mr-3 h-5 w-5" />
          <input
            v-if="searchable"
            ref="my-input"
            v-model="searchTerm"
            class="bg-inherit h-5 min-w-0 flex-1 truncate border-none p-0 text-sm focus:border-none focus:outline-none focus:ring-0"
            :class="[
              props.disabled
                ? 'cursor-not-allowed text-black-400'
                : selectedOption?.isPlaceholderOption
                  ? 'text-black-300'
                  : 'text-black-900',
            ]"
            :data-testid="TEST_IDS_CREATE.singleSelect.input"
            :disabled="props.disabled ?? false"
            :placeholder="placeholder ?? ''"
            type="text"
            @keydown="onKeyDown"
          />
          <span
            v-else
            class="h-5 truncate"
            :class="!selectedOption || selectedOption.isPlaceholderOption ? 'text-black-300' : ''"
          >
            {{ selectedOption?.optionLabel || placeholder }}
          </span>
        </div>
      </div>
      <ChevronDownIcon
        aria-hidden="true"
        class="absolute right-2 aspect-square shrink-0 text-current"
        :class="[small ? 'w-4' : 'w-5']"
      />
    </MenuButton>

    <Teleport :disabled="!teleportDropdown" :to="dropdownTarget">
      <Transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
        @after-enter="onAfterEnter"
        @after-leave="onAfterLeave"
        @before-enter="onDropdownEnter"
      >
        <MenuItems
          class="items z-20 max-h-60 w-fit divide-y divide-black-100 overflow-y-auto rounded-md bg-white py-2.5 shadow-lg focus:outline-none"
          :class="[
            teleportDropdown ? '' : 'absolute min-w-full',
            props.direction === 'left' ? 'origin-top-left' : 'origin-top-right',
            teleportDropdown ? '' : props.direction === 'left' ? 'left-0' : 'right-0',
          ]"
          :data-testid="TEST_IDS_CREATE.singleSelect.options"
          :style="teleportDropdown ? dropdownStyle : undefined"
        >
          <div v-for="(section, index) in filteredOptions" :key="index" class="space-y-1.5 py-1.5">
            <template v-for="option in section" :key="option.value">
              <MenuItem v-slot="{ active }">
                <a
                  class="group flex cursor-pointer items-center gap-3 px-5 py-2 text-sm"
                  :class="[
                    option === selectedOption || active
                      ? 'bg-lochmara-50 text-lochmara-600'
                      : 'text-black-900',
                  ]"
                  @click="() => onItemSelected(option.optionValue)"
                >
                  <AvatarOrganization
                    v-if="isOrganization && !option.isHideIcon && option.image"
                    :image="option.image"
                    size="small"
                  />
                  <component
                    :is="option.icon"
                    v-else-if="option.icon && !option.isHideIcon"
                    aria-hidden="true"
                    class="text-black-400 group-hover:text-current"
                    :image="isResponsibleEmployee ? option.image : undefined"
                    :is-responsible-employee="isResponsibleEmployee"
                    :name="option.optionLabel"
                    size="small"
                  />

                  <div
                    v-else-if="existsAtLeastOneIconInOptions || isOrganization"
                    class="w-6"
                  ></div>
                  <span class="block w-full whitespace-normal break-words text-left">
                    {{ option.optionLabel }}
                  </span>
                </a>
              </MenuItem>
            </template>
          </div>
          <MenuItem v-if="filteredOptions.length === 0 && searchTerm !== ''">
            <div class="px-5 py-2 text-sm">
              <span>{{ tfDefault('common:noResults') }}</span>
            </div>
          </MenuItem>

          <MenuItem v-if="showEmptyStateAction" key="menu-item-2">
            <div class="px-5 py-2">
              <ButtonTertiary
                :label="tfDefault(props.emptyStateAction?.labelTranslationKey ?? '')"
                @click="onEmptyStateActionClick"
              >
                <component :is="props.emptyStateAction?.icon" class="mr-3 h-6 w-6 text-current" />
              </ButtonTertiary>
            </div>
          </MenuItem>
        </MenuItems>
      </Transition>
    </Teleport>
  </Menu>
</template>

<style scoped>
.menu:has(.items) {
  position: relative;
}
</style>
